// Formula text as an .xlsx stores it vs as a person types it. Newer
// functions are stored with prefixes ("_xlfn.XLOOKUP", "_xlfn._xlws.FILTER",
// "_xlpm.x" for LAMBDA parameters): reading takes them off, writing puts them
// back so older Excel shows #NAME? instead of misreading and newer Excel
// knows them.

// functions Excel stores as _xlfn.<NAME> (those added after Excel 2007)
export const FUTURE = new Set(
  [
    "AGGREGATE", "ARABIC", "BASE", "BINOM.DIST", "BINOM.INV", "BITAND", "BITOR", "BITXOR", "CEILING.MATH",
    "CEILING.PRECISE", "CHISQ.DIST", "CHISQ.INV", "CHOOSECOLS", "CHOOSEROWS", "COMBINA", "CONCAT", "CONFIDENCE.NORM",
    "COT", "COTH", "COVARIANCE.P", "COVARIANCE.S", "CSC", "DAYS", "DECIMAL", "DROP", "ERF.PRECISE", "EXPAND",
    "EXPON.DIST", "F.DIST", "F.INV", "FILTERXML", "FLOOR.MATH", "FLOOR.PRECISE", "FORECAST.ETS", "FORECAST.LINEAR",
    "FORMULATEXT", "GAMMA", "GAMMA.DIST", "GAMMA.INV", "GAMMALN.PRECISE", "GAUSS", "HSTACK", "IFNA", "IFS",
    "IMAGE", "ISFORMULA", "ISOMITTED", "ISOWEEKNUM", "LAMBDA", "LET", "LOGNORM.DIST", "LOGNORM.INV", "MAKEARRAY",
    "MAP", "MAXIFS", "MINIFS", "MODE.MULT", "MODE.SNGL", "MUNIT", "NEGBINOM.DIST", "NORM.DIST", "NORM.INV",
    "NORM.S.DIST", "NORM.S.INV", "NUMBERVALUE", "PERCENTILE.EXC", "PERCENTILE.INC", "PERCENTRANK.EXC",
    "PERCENTRANK.INC", "PERMUTATIONA", "PHI", "POISSON.DIST", "QUARTILE.EXC", "QUARTILE.INC", "RANDARRAY",
    "RANK.AVG", "RANK.EQ", "REDUCE", "RRI", "SCAN", "SEC", "SECH", "SEQUENCE", "SHEET", "SHEETS", "SKEW.P",
    "SORTBY", "STDEV.P", "STDEV.S", "SWITCH", "T.DIST", "T.DIST.2T", "T.DIST.RT", "T.INV", "T.INV.2T", "T.TEST",
    "TAKE", "TEXTAFTER", "TEXTBEFORE", "TEXTJOIN", "TEXTSPLIT", "TOCOL", "TOROW", "UNICHAR", "UNICODE", "UNIQUE",
    "VAR.P", "VAR.S", "VSTACK", "WEIBULL.DIST", "WRAPCOLS", "WRAPROWS", "XLOOKUP", "XMATCH", "XOR", "Z.TEST",
    "BYCOL", "BYROW", "ARRAYTOTEXT", "VALUETOTEXT",
  ],
);
// these are stored as _xlfn._xlws.<NAME>
export const WORKSHEET = new Set(["FILTER", "SORT"]);

/** Walks the formula outside "strings" and 'sheet names' */
function mapOutsideQuotes(f: string, fn: (part: string) => string): string {
  let out = "";
  let i = 0;
  while (i < f.length) {
    const c = f[i];
    if (c === '"' || c === "'") {
      let j = i + 1;
      while (j < f.length) {
        if (f[j] === c) {
          if (f[j + 1] === c) {
            j += 2;
            continue;
          }
          break;
        }
        j++;
      }
      out += f.slice(i, j + 1);
      i = j + 1;
    } else {
      let j = i;
      while (j < f.length && f[j] !== '"' && f[j] !== "'") j++;
      out += fn(f.slice(i, j));
      i = j;
    }
  }
  return out;
}

/** "_xlfn._xlws.FILTER(..)" -> "FILTER(..)" */
export function fromFileFormula(f: string): string {
  return mapOutsideQuotes(f, (part) => part.replace(/_xlfn\.(_xlws\.)?/gi, "").replace(/_xlpm\./gi, ""));
}

interface Tok {
  kind: "id" | "str" | "other";
  at: number;
  end: number;
  text: string;
}

const ID_START = /[\p{L}_\\]/u;
const ID_PART = /[\p{L}\p{N}_.\\]/u;

function tokens(f: string): Tok[] {
  const out: Tok[] = [];
  let i = 0;
  while (i < f.length) {
    const c = f[i] ?? "";
    if (c === '"' || c === "'") {
      let j = i + 1;
      while (j < f.length) {
        if (f[j] === c) {
          if (f[j + 1] === c) {
            j += 2;
            continue;
          }
          break;
        }
        j++;
      }
      out.push({ kind: "str", at: i, end: j + 1, text: f.slice(i, j + 1) });
      i = j + 1;
    } else if (ID_START.test(c)) {
      let j = i + 1;
      while (j < f.length && ID_PART.test(f[j] ?? "")) j++;
      out.push({ kind: "id", at: i, end: j, text: f.slice(i, j) });
      i = j;
    } else {
      out.push({ kind: "other", at: i, end: i + 1, text: c });
      i++;
    }
  }
  return out;
}

/** LET and LAMBDA parameters (and their uses inside the call that binds
    them) as the file format writes them: _xlpm.x. Excel reads LET(x, ...)
    in a file as a defined name x otherwise, which is #NAME?. */
function markParams(f: string): string {
  const t = tokens(f);
  const scopes: { from: number; to: number; names: Set<string> }[] = [];
  for (let k = 0; k < t.length; k++) {
    const tk = t[k];
    const open = t[k + 1];
    if (!tk || tk.kind !== "id" || open?.text !== "(") continue;
    const fn = tk.text.toUpperCase().replace(/^_XLFN\./, "");
    if (fn !== "LET" && fn !== "LAMBDA") continue;
    // the arguments at depth 1, up to the matching ")"
    const args: Tok[][] = [[]];
    let depth = 0;
    let close = t.length;
    for (let j = k + 1; j < t.length; j++) {
      const x = t[j];
      if (!x) break;
      if (x.text === "(" || x.text === "{") depth++;
      else if (x.text === ")" || x.text === "}") {
        depth--;
        if (depth === 0) {
          close = j;
          break;
        }
      } else if (x.text === "," && depth === 1) {
        args.push([]);
        continue;
      }
      if (j > k + 1) args[args.length - 1]?.push(x);
    }
    const names = new Set<string>();
    args.slice(0, -1).forEach((a, i) => {
      const words = a.filter((x) => x.text.trim() !== "");
      const name = words.length === 1 && words[0]?.kind === "id" ? words[0].text : null;
      if (name && (fn === "LAMBDA" || i % 2 === 0)) names.add(name.replace(/^_xlpm\./i, "").toUpperCase());
    });
    if (names.size) scopes.push({ from: open.at, to: t[close]?.end ?? f.length, names });
  }
  if (!scopes.length) return f;
  let out = "";
  let last = 0;
  for (let k = 0; k < t.length; k++) {
    const x = t[k];
    if (!x || x.kind !== "id" || /^_xlpm\./i.test(x.text)) continue;
    const prev = t[k - 1];
    const next = t[k + 1];
    if (next?.text === "!" || prev?.text === "!") continue; // a sheet name, or a cell after one
    const up = x.text.toUpperCase();
    if (!scopes.some((sc) => x.at > sc.from && x.at < sc.to && sc.names.has(up))) continue;
    out += f.slice(last, x.at) + "_xlpm." + x.text;
    last = x.end;
  }
  return out + f.slice(last);
}

/** "XLOOKUP(..)" -> "_xlfn.XLOOKUP(..)", and LET/LAMBDA parameters as
    _xlpm.x, as Excel writes them in a file. */
export function toFileFormula(f: string): string {
  return mapOutsideQuotes(markParams(f), (part) =>
    part.replace(/(^|[^A-Za-z0-9_.!\\])([A-Za-z][A-Za-z0-9.]*)(?=\s*\()/g, (all, before: string, name: string) => {
      const up = name.toUpperCase();
      if (WORKSHEET.has(up)) return `${before}_xlfn._xlws.${name}`;
      if (FUTURE.has(up)) return `${before}_xlfn.${name}`;
      return all;
    }),
  );
}
